import { useEffect, useState } from 'react';
import { api, ErroApi, type IndicacaoAdmin, type SaqueAdmin } from '../../api';
import {
  Campo, Carregando, Erro, Modal, Selo, Tabela, Vazio, cnpjFormatado,
} from '../../componentes/ui';
import type { Empresa } from '../../tipos';

const moeda = (v: number | string) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataCurta = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

const STATUS_INDICACAO: Record<string, string> = { pendente: 'neutro', convertida: 'ok', cancelada: 'inativo' };
const STATUS_SAQUE: Record<string, string> = { solicitado: 'neutro', pago: 'ok', recusado: 'inativo' };

/**
 * O administrador decide o que é dinheiro de verdade: qual indicação virou
 * cliente (e por quanto mês a mensalidade dele entra no cálculo) e qual
 * saque pagar. Nada aqui é automático de propósito.
 */
export function Comissoes() {
  const [indicacoes, setIndicacoes] = useState<IndicacaoAdmin[] | null>(null);
  const [saques, setSaques] = useState<SaqueAdmin[] | null>(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [convertendo, setConvertendo] = useState<IndicacaoAdmin | null>(null);

  async function carregar() {
    try {
      const [i, s] = await Promise.all([api.adminIndicacoes(), api.adminSaques()]);
      setIndicacoes(i.indicacoes);
      setSaques(s.saques);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar.');
    }
  }
  useEffect(() => { void carregar(); }, []);

  async function agir(fn: () => Promise<unknown>) {
    setErro('');
    setOcupado(true);
    try {
      await fn();
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui concluir.');
    } finally {
      setOcupado(false);
    }
  }

  const pendentes = indicacoes?.filter((i) => i.status === 'pendente') ?? [];
  const resolvidas = indicacoes?.filter((i) => i.status !== 'pendente') ?? [];
  const saquesAbertos = saques?.filter((s) => s.status === 'solicitado') ?? [];
  const saquesResolvidos = saques?.filter((s) => s.status !== 'solicitado') ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Comissões</h1>
          <p>
            Indicações de clientes feitas por contadores e funcionários de escritório, e os saques da
            comissão acumulada (3% da mensalidade, por mês, por indicação convertida).
          </p>
        </div>
      </header>

      <Erro>{erro}</Erro>

      {!indicacoes || !saques ? <Carregando /> : (
        <>
          {saquesAbertos.length > 0 && (
            <section>
              <h2>Saques pedidos</h2>
              <Tabela
                itens={saquesAbertos}
                chaveDe={(s) => s.id}
                colunas={[
                  { rotulo: 'Quem pediu', principal: true, celula: (s) => <strong>{s.usuario.nome}</strong> },
                  { rotulo: 'Celular', mono: true, fraco: true, celula: (s) => s.usuario.celular },
                  { rotulo: 'Valor', numero: true, celula: (s) => moeda(s.valor) },
                  { rotulo: 'Pedido em', fraco: true, celula: (s) => dataCurta(s.criadoEm) },
                  {
                    rotulo: 'Ações', acoes: true,
                    celula: (s) => (
                      <>
                        <button className="btn btn-primary btn-sm" disabled={ocupado}
                          onClick={() => agir(() => api.resolverSaque(s.id, 'pagar'))}>Marcar pago</button>
                        <button className="link-botao" disabled={ocupado}
                          onClick={() => agir(() => api.resolverSaque(s.id, 'recusar'))}>Recusar</button>
                      </>
                    ),
                  },
                ]}
              />
            </section>
          )}

          <section style={{ marginTop: saquesAbertos.length ? 28 : 0 }}>
            <h2>Indicações pendentes</h2>
            {pendentes.length === 0 ? (
              <Vazio titulo="Nenhuma indicação esperando" />
            ) : (
              <Tabela
                itens={pendentes}
                chaveDe={(i) => i.id}
                colunas={[
                  { rotulo: 'Indicado', principal: true, celula: (i) => <strong>{i.nomeProspecto}</strong> },
                  { rotulo: 'Celular', mono: true, fraco: true, celula: (i) => i.celularProspecto ?? '—' },
                  { rotulo: 'Quem indicou', fraco: true, celula: (i) => `${i.usuario.nome} · ${i.usuario.celular}` },
                  { rotulo: 'Data', fraco: true, celula: (i) => dataCurta(i.criadoEm) },
                  {
                    rotulo: 'Ações', acoes: true,
                    celula: (i) => (
                      <>
                        <button className="btn btn-primary btn-sm" disabled={ocupado}
                          onClick={() => setConvertendo(i)}>Virou cliente</button>
                        <button className="link-botao" disabled={ocupado}
                          onClick={() => agir(() => api.cancelarIndicacao(i.id))}>Cancelar</button>
                      </>
                    ),
                  },
                ]}
              />
            )}
          </section>

          <section style={{ marginTop: 28 }}>
            <h2>Histórico</h2>
            {resolvidas.length === 0 && saquesResolvidos.length === 0 ? (
              <Vazio titulo="Nada resolvido ainda" />
            ) : (
              <>
                {resolvidas.length > 0 && (
                  <Tabela
                    itens={resolvidas}
                    chaveDe={(i) => i.id}
                    colunas={[
                      { rotulo: 'Indicado', principal: true, celula: (i) => <strong>{i.nomeProspecto}</strong> },
                      { rotulo: 'Quem indicou', fraco: true, celula: (i) => i.usuario.nome },
                      { rotulo: 'Virou', fraco: true, celula: (i) => i.empresa ? `${i.empresa.razaoSocial} · ${cnpjFormatado(i.empresa.cnpj)}` : '—' },
                      { rotulo: 'Status', celula: (i) => <Selo tipo={STATUS_INDICACAO[i.status]} texto={i.status} /> },
                    ]}
                  />
                )}
                {saquesResolvidos.length > 0 && (
                  <Tabela
                    itens={saquesResolvidos}
                    chaveDe={(s) => s.id}
                    colunas={[
                      { rotulo: 'Quem pediu', principal: true, celula: (s) => <strong>{s.usuario.nome}</strong> },
                      { rotulo: 'Valor', numero: true, celula: (s) => moeda(s.valor) },
                      { rotulo: 'Resolvido em', fraco: true, celula: (s) => s.resolvidoEm ? dataCurta(s.resolvidoEm) : '—' },
                      { rotulo: 'Status', celula: (s) => <Selo tipo={STATUS_SAQUE[s.status]} texto={s.status} /> },
                    ]}
                  />
                )}
              </>
            )}
          </section>
        </>
      )}

      {convertendo && (
        <ConverterIndicacao
          indicacao={convertendo}
          onFechar={() => setConvertendo(null)}
          onConvertida={() => { setConvertendo(null); void carregar(); }}
        />
      )}
    </div>
  );
}

function ConverterIndicacao({
  indicacao, onFechar, onConvertida,
}: { indicacao: IndicacaoAdmin; onFechar: () => void; onConvertida: () => void }) {
  const [empresas, setEmpresas] = useState<Empresa[] | null>(null);
  const [empresaId, setEmpresaId] = useState<number | ''>('');
  const [mensalidade, setMensalidade] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api.empresas().then((r) => setEmpresas(r.empresas)).catch(() => setEmpresas([]));
  }, []);

  async function salvar() {
    if (!empresaId) { setErro('Escolha a empresa.'); return; }
    setErro('');
    setSalvando(true);
    try {
      await api.converterIndicacao(indicacao.id, empresaId);
      if (mensalidade.trim()) {
        await api.definirMensalidade(empresaId, Number(mensalidade.replace(',', '.')));
      }
      onConvertida();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      titulo="Indicação virou cliente"
      descricao={`"${indicacao.nomeProspecto}", indicado por ${indicacao.usuario.nome}.`}
      onFechar={onFechar}
    >
      <Erro>{erro}</Erro>
      <Campo rotulo="Empresa cadastrada no Conti9">
        {!empresas ? <Carregando /> : (
          <select className="input" value={empresaId} onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Selecione…</option>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>{e.razaoSocial} · {cnpjFormatado(e.cnpj)}</option>
            ))}
          </select>
        )}
      </Campo>
      <Campo
        rotulo="Mensalidade (R$)"
        ajuda="Base para calcular os 3% todo mês. Deixe em branco para definir depois, em Empresas."
      >
        <input className="input" value={mensalidade} onChange={(e) => setMensalidade(e.target.value)}
          placeholder="0,00" inputMode="decimal" />
      </Campo>

      <div className="modal-acoes">
        <button className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando || !empresaId}>
          {salvando ? <span className="spinner" /> : 'Confirmar'}
        </button>
      </div>
    </Modal>
  );
}
